import { useMemo, useState } from 'react';
import type { V2AiObservation } from '../../domain/aiObservation';
import type { V2Area, V2ChecklistItem, V2Inspection } from '../../domain/inspection';
import { useT } from '../../i18n/i18n';
import { aiConfidenceLabel, aiVerdictNote, gateNoteText, itemTitleFor } from '../../i18n/labels';
import { acceptLimitationPromptAction, aiStore, fileObservationToItemAction, ignoreObservationAction, observationAllowsAction, observationCanBeFiled, observationPresentationOf, recordVoluntaryObservationAction, unclassifiedObservationsOfArea } from '../../state/aiStore';
import { useStore } from '../../state/createStore';
import { Button, Card, Icon, SectionEyebrow, SheetShell, TextArea, showToast } from '../../ui/components';

// AI发现 N 项未归类 (Final Owner Decision 2026-09-14, CA-5): the ONE place an AI observation the AI could
// not match to a checklist row appears, once, with exactly three things the inspector can do —
//   归入现有检查项   the observation (and its photos) join a row of this area
//   记为其他问题     the inspector writes an other finding (the area's editor, pre-filled)
//   忽略             kept in the trail, never in the report
// NY Scope Gate (OD-13) shapes the entries it decided on: an out-of-scope subject can never be filed
// under a row (记为其他问题 records it as a voluntary observation); a concealed in-scope component is
// recorded as the row's limitation (记录检查限制). Nothing here ever creates a checklist row.
export function AiUnclassified(props: { inspection: V2Inspection; area: V2Area; items: V2ChecklistItem[]; locked: boolean; onRecordAsOther: (observation: V2AiObservation) => void }) {
  const { t, locale } = useT();
  const aiState = useStore(aiStore, (s) => s);
  const list = useMemo(() => unclassifiedObservationsOfArea(aiState, props.area.id), [aiState, props.area.id]);
  const [fileFor, setFileFor] = useState<V2AiObservation | null>(null);
  const [voluntaryFor, setVoluntaryFor] = useState<V2AiObservation | null>(null);
  const [limitFor, setLimitFor] = useState<V2AiObservation | null>(null);
  const [text, setText] = useState('');
  // 2026-10-28 (device walkthrough A2: seven entries × three buttons were a screen and a half): ONE line with
  // the count until the inspector opens it, in place. Still the one place these entries appear (CA-5).
  const [open, setOpen] = useState(false);
  if (list.length === 0) return null;

  async function file(observation: V2AiObservation, item: V2ChecklistItem) {
    setFileFor(null);
    const outcome = await fileObservationToItemAction(observation.id, item.id);
    if (outcome.ok) showToast(t('ai.filedToast', { name: itemTitleFor(locale, item) }));
    else if (outcome.reason === 'outOfScope') showToast(t('ai.notFilable'), 'info');
  }

  async function saveVoluntary() {
    const observation = voluntaryFor;
    const body = text.trim();
    setVoluntaryFor(null);
    setText('');
    if (!observation || body.length === 0) return;
    const outcome = await recordVoluntaryObservationAction(observation.id, { observation: body, title: observation.title });
    if (outcome.ok) showToast(t('ai.scopeReminder.savedToast'));
  }

  async function saveLimitation() {
    const observation = limitFor;
    const extent = text.trim();
    setLimitFor(null);
    setText('');
    if (!observation) return;
    const outcome = await acceptLimitationPromptAction(observation.id, { extent });
    if (outcome.ok) showToast(t('ai.limitation.savedToast'));
  }

  async function ignore(observation: V2AiObservation) {
    await ignoreObservationAction(observation.id);
    showToast(t('ai.ignoredToast'), 'info');
  }

  return (
    <>
      <Card className={`ai-unclassified ${open ? 'ai-unclassified-open' : ''}`} testId="ai-unclassified">
        <button type="button" className="ai-unclassified-toggle" onClick={() => setOpen((v) => !v)} aria-expanded={open} data-testid="ai-unclassified-toggle" data-count={list.length}>
          <span className="sparkle-inline">✦</span>
          <span className="ai-unclassified-toggle-label">{t('ai.unclassifiedTitle', { n: list.length })}</span>
          <span className="ai-unclassified-toggle-action">{open ? t('area.unclassifiedHide') : t('area.unclassifiedShow')}</span>
          <Icon name={open ? 'keyboard_arrow_up' : 'chevron_right'} />
        </button>
        {open ? <p className="card-note">{t('ai.unclassifiedHint')}</p> : null}
        {(open ? list : []).map((o) => {
          const presentation = observationPresentationOf(o);
          const target = presentation === 'limitationPrompt' ? (props.items.find((i) => i.id === (o.scope?.limitation?.targetItemId ?? '')) ?? null) : null;
          const canFile = observationCanBeFiled(o);
          const canOther = canFile;
          const canVoluntary = presentation === 'scopeReminder' && observationAllowsAction(o, 'voluntaryObservation');
          const noteLines = (o.scope?.noteCodes ?? []).map((code) => gateNoteText(t, code));
          const verdictNote = aiVerdictNote(t, o.verdict);
          // Owner on the emulator 2026-10-21 (「标题都会重复哪怕文字不一样也是属于重复。而且每一个都重复」): the card
          // printed the AI's `title` AND its `headline`, two wordings of the same problem, on every entry. One
          // line now — the headline (the problem and the place, 2026-10-19), the title only when there is
          // none — then the one-sentence observation, as on the item sheet.
          const headline = o.headline.trim();
          const lead = presentation === 'limitationPrompt' && target ? itemTitleFor(locale, target) : headline.length > 0 ? headline : o.title;
          const body = (o.observationUi ?? o.observation).trim();
          const limitationHeadline = presentation === 'limitationPrompt' && target && headline.length > 0 ? headline : null;
          return (
            <div key={o.id} className={`ai-unclassified-entry ai-unclassified-${presentation}`} data-testid="ai-unclassified-entry" data-presentation={presentation} data-observation-id={o.id} data-verdict={o.verdict ?? ''}>
              <p className="ai-unclassified-title" data-testid="ai-unclassified-lead">
                {presentation === 'limitationPrompt' ? <Icon name="visibility_off" /> : null}
                {lead}
              </p>
              {presentation === 'scopeReminder' ? <SectionEyebrow>{t('ai.scopeReminder.title')}</SectionEyebrow> : null}
              {presentation === 'limitationPrompt' ? <SectionEyebrow>{t('ai.limitation.title')}</SectionEyebrow> : null}
              {limitationHeadline ? <p className="card-body">{limitationHeadline}</p> : null}
              {body.length > 0 && body !== lead && body !== limitationHeadline ? <p className="card-note">{body}</p> : null}
              {o.concern.length > 0 ? (
                <p className="card-note">
                  <strong>{t('ai.concern')}</strong> · {o.concern}
                </p>
              ) : null}
              {/* 2026-10-18: the confidence beside every observation; the one line for a conclusion photos cannot settle. */}
              <div className="ai-observation-meta">
                <span className="ai-confidence" data-testid="ai-unclassified-confidence" data-confidence={o.confidence}>
                  {aiConfidenceLabel(t, o.confidence)}
                </span>
              </div>
              {verdictNote ? (
                <p className="card-note ai-verdict-note" data-testid="ai-unclassified-verdict-note" data-verdict={o.verdict ?? ''}>
                  {verdictNote}
                </p>
              ) : null}
              {presentation === 'scopeReminder' ? <p className="card-note">{t('ai.scopeReminder.body')}</p> : null}
              {presentation === 'limitationPrompt' ? <p className="card-note">{t('ai.limitation.body')}</p> : null}
              {noteLines.map((line) => (
                <p className="card-note scope-card-note" key={line}>
                  {line}
                </p>
              ))}
              {!props.locked ? (
                <div className="btn-row ai-unclassified-actions">
                  {canFile ? (
                    <Button variant="secondary" size="small" icon="checklist" onClick={() => setFileFor(o)} testId="ai-file-to-item">
                      {t('ai.fileToItem')}
                    </Button>
                  ) : null}
                  {presentation === 'limitationPrompt' ? (
                    <Button
                      variant="secondary"
                      size="small"
                      icon="edit"
                      onClick={() => {
                        setText(o.headline);
                        setLimitFor(o);
                      }}
                      testId="ai-record-limitation"
                    >
                      {t('ai.limitation.record')}
                    </Button>
                  ) : null}
                  {canOther ? (
                    <Button variant="secondary" size="small" icon="flag" onClick={() => props.onRecordAsOther(o)} testId="ai-record-other">
                      {t('ai.recordAsOther')}
                    </Button>
                  ) : null}
                  {canVoluntary ? (
                    <Button
                      variant="secondary"
                      size="small"
                      icon="flag"
                      onClick={() => {
                        setText('');
                        setVoluntaryFor(o);
                      }}
                      testId="ai-record-other"
                    >
                      {t('ai.recordAsOther')}
                    </Button>
                  ) : null}
                  <Button variant="text" size="small" onClick={() => void ignore(o)} testId="ai-ignore">
                    {t('ai.ignore')}
                  </Button>
                </div>
              ) : null}
            </div>
          );
        })}
      </Card>

      {fileFor ? (
        <SheetShell title={t('ai.filePickTitle')} subtitle={fileFor.title} onClose={() => setFileFor(null)} layer="over" testId="ai-file-picker">
          <div className="option-list" role="listbox">
            {props.items.map((item) => (
              <button key={item.id} type="button" role="option" aria-selected={false} className="option-row" onClick={() => void file(fileFor, item)} data-testid="ai-file-option" data-name={item.title}>
                {itemTitleFor(locale, item)}
              </button>
            ))}
          </div>
        </SheetShell>
      ) : null}

      {voluntaryFor ? (
        <SheetShell
          title={t('ai.recordAsOther')}
          subtitle={voluntaryFor.title}
          onClose={() => setVoluntaryFor(null)}
          testId="scope-record-sheet"
          footer={
            <>
              <Button onClick={() => void saveVoluntary()} disabled={text.trim().length === 0} testId="scope-record-save">
                {t('common.save')}
              </Button>
              <Button variant="text" onClick={() => setVoluntaryFor(null)}>
                {t('shell.cancel')}
              </Button>
            </>
          }
        >
          <p className="card-note">{t('ai.scopeReminder.body')}</p>
          <SectionEyebrow>{t('ai.scopeReminder.observationLabel')}</SectionEyebrow>
          <TextArea value={text} onChange={setText} rows={4} testId="scope-record-text" />
        </SheetShell>
      ) : null}

      {limitFor ? (
        <SheetShell
          title={t('ai.limitation.record')}
          onClose={() => setLimitFor(null)}
          testId="limitation-sheet"
          footer={
            <>
              <Button onClick={() => void saveLimitation()} testId="limitation-save">
                {t('common.save')}
              </Button>
              <Button variant="text" onClick={() => setLimitFor(null)}>
                {t('shell.cancel')}
              </Button>
            </>
          }
        >
          <p className="card-note">{t('ai.limitation.body')}</p>
          <SectionEyebrow>{t('ai.limitation.extentLabel')}</SectionEyebrow>
          <TextArea value={text} onChange={setText} rows={3} testId="limitation-extent" />
        </SheetShell>
      ) : null}
    </>
  );
}
